/** Shared acorn parsing for the source rewrites. */
import { type Node, parse } from "acorn";

// biome-ignore lint/suspicious/noExplicitAny: ESTree nodes are walked generically
export type AnyNode = Node & Record<string, any>;

/** How to parse: a classic script, or an ES module (strict, top-level await, imports). */
export type SourceType = "script" | "module";

/** Why code didn't parse. `line` and `column` are 1-based, like stack-trace sites. */
export interface ParseError {
  message: string;
  line: number;
  column: number;
  /** Offset of the error in the code. */
  offset: number;
}

export type ParseResult =
  | { program: AnyNode; error?: undefined }
  | { program?: undefined; error: ParseError };

export function parseProgram(code: string, sourceType: SourceType = "script"): ParseResult {
  try {
    const program = parse(code, {
      ecmaVersion: "latest",
      sourceType,
      allowHashBang: true,
      locations: true,
    }) as AnyNode;
    return { program };
  } catch (e) {
    const err = e as { message?: unknown; pos?: unknown; loc?: { line: number; column: number } };
    if (typeof err.pos !== "number" || !err.loc) throw e;
    return {
      error: {
        // acorn appends " (line:column)"; the position is reported separately.
        message: String(err.message).replace(/ \(\d+:\d+\)$/, ""),
        line: err.loc.line,
        column: err.loc.column + 1,
        offset: err.pos,
      },
    };
  }
}

export const isNode = (v: unknown): v is AnyNode =>
  typeof v === "object" && v !== null && typeof (v as { type?: unknown }).type === "string";

const SKIP_KEYS = new Set(["type", "start", "end", "loc", "range"]);

/** A node's child nodes. */
export function children(node: AnyNode): AnyNode[] {
  const out: AnyNode[] = [];
  for (const key of Object.keys(node)) {
    if (SKIP_KEYS.has(key)) continue;
    const value = node[key];
    if (Array.isArray(value)) out.push(...value.filter(isNode));
    else if (isNode(value)) out.push(value);
  }
  return out;
}

/** Names bound by a binding pattern. */
export function patternNames(pattern: AnyNode | null | undefined, into: Set<string>): void {
  if (!pattern) return;
  switch (pattern.type) {
    case "Identifier":
      into.add(pattern.name);
      break;
    case "ObjectPattern":
      for (const p of pattern.properties)
        patternNames(p.type === "RestElement" ? p : p.value, into);
      break;
    case "ArrayPattern":
      for (const p of pattern.elements) patternNames(p, into);
      break;
    case "AssignmentPattern":
      patternNames(pattern.left, into);
      break;
    case "RestElement":
      patternNames(pattern.argument, into);
      break;
  }
}
